import { deriveSeed, Rng } from "@eldrun/shared";
import type { MusicState } from "./audio-model.ts";

/**
 * Partitura procedural (doc 05, "Estados de música"): cada estado tem andamento, modo e camadas;
 * os motivos saem da seed da partida (cada ilha tem trilha própria) e a mesma seed dá a mesma
 * música. Puro: devolve as notas de um compasso (4 tempos) para o sequenciador agendar.
 */
export type Instrument = "zabumba" | "triangulo" | "sanfona" | "viola" | "pifano" | "drone" | "baixo" | "bumbo" | "caixa";

export interface NoteEvent {
  /** Tempo dentro do compasso (0 a 4) e duração, em tempos. */
  t: number;
  dur: number;
  inst: Instrument;
  /** Altura MIDI (percussão ignora). */
  midi: number;
  vel: number;
}

const MODES = {
  mixolidio: [0, 2, 4, 5, 7, 9, 10],
  maior: [0, 2, 4, 5, 7, 9, 11],
  eolio: [0, 2, 3, 5, 7, 8, 10],
  dorico: [0, 2, 3, 5, 7, 9, 10],
  frigio: [0, 1, 3, 5, 7, 8, 10],
} as const;
type Mode = keyof typeof MODES;

interface StateDef {
  bpm: number;
  mode: Mode;
  /** Tônica (MIDI). */
  root: number;
}

/** Caráter de cada estado: baião em mixolídio de dia, menor à tarde e à noite, frígio no chefe (lobby e combate: reserva das faixas gravadas, F84). */
export const MUSIC: Record<Exclude<MusicState, "silencio">, StateDef> = {
  menu: { bpm: 90, mode: "mixolidio", root: 55 },
  lobby: { bpm: 90, mode: "mixolidio", root: 55 },
  combate: { bpm: 112, mode: "eolio", root: 52 },
  dia: { bpm: 96, mode: "mixolidio", root: 55 },
  entardecer: { bpm: 80, mode: "eolio", root: 55 },
  noite: { bpm: 70, mode: "dorico", root: 50 },
  chefe: { bpm: 132, mode: "frigio", root: 52 },
  encanto: { bpm: 112, mode: "dorico", root: 57 },
  cerco: { bpm: 104, mode: "eolio", root: 52 },
  travessia: { bpm: 120, mode: "mixolidio", root: 53 },
  vitoria: { bpm: 112, mode: "maior", root: 60 },
  derrota: { bpm: 60, mode: "eolio", root: 50 },
  espectador: { bpm: 66, mode: "dorico", root: 50 },
};

/** Grau da escala (com oitavas) para MIDI. */
export function degree(state: StateDef, d: number): number {
  const scale = MODES[state.mode];
  const octave = Math.floor(d / scale.length);
  const i = ((d % scale.length) + scale.length) % scale.length;
  return state.root + 12 * octave + (scale[i] ?? 0);
}

interface Motif {
  /** Graus e posições (em oitavos) de um motivo de 2 compassos. */
  notes: { d: number; at: number; len: number }[];
}

const motifCache = new Map<string, Motif>();

/** Motivo de 2 compassos tirado da seed (passos curtos, cadência na tônica). */
export function motif(seed: number, name: string): Motif {
  const key = `${seed}:${name}`;
  const cached = motifCache.get(key);
  if (cached) return cached;
  const rng = new Rng(deriveSeed(seed, `musica-${name}`, 0));
  const notes: Motif["notes"] = [];
  let d = rng.int(0, 4);
  let at = 0;
  while (at < 15) {
    const len = rng.pick([1, 1, 2, 2, 3]);
    notes.push({ d, at, len: Math.min(len, 16 - at) });
    at += len;
    d = Math.max(-2, Math.min(9, d + rng.pick([-2, -1, -1, 1, 1, 2, 3])));
  }
  // Fecha na tônica.
  const last = notes[notes.length - 1];
  if (last) last.d = 0;
  const m = { notes };
  if (motifCache.size > 64) motifCache.clear();
  motifCache.set(key, m);
  return m;
}

/** Frase de 8 compassos em silêncio? De dia, ~40 % das frases são só ambiente (doc 05). */
export function silentPhrase(seed: number, state: MusicState, bar: number): boolean {
  if (state !== "dia") return false;
  const phrase = Math.floor(bar / 8);
  return new Rng(deriveSeed(seed, "musica-silencio", phrase)).chance(0.4);
}

/** Andamento efetivo: a Pedra de Encanto acelera; o Cerco acelera com a intensidade. */
export function tempo(state: Exclude<MusicState, "silencio">, bar: number, intensity: number): number {
  const base = MUSIC[state].bpm;
  if (state === "encanto") return Math.min(160, base + bar * 3);
  if (state === "cerco") return base + Math.round(40 * Math.max(0, Math.min(1, intensity)));
  return base;
}

function melody(def: StateDef, m: Motif, bar: number, inst: Instrument, vel: number, octave = 12, out: NoteEvent[]): void {
  const half = bar % 2;
  for (const n of m.notes) {
    if (Math.floor(n.at / 8) !== half) continue;
    out.push({ t: (n.at % 8) / 2, dur: n.len / 2, inst, midi: degree(def, n.d) + octave, vel });
  }
}

/**
 * Notas de um compasso. `intensity` (0 a 1): assombrações perto à noite, raio do Cerco, fase do
 * chefe; mais camadas entram com ela.
 */
export function scoreBar(state: MusicState, bar: number, seed: number, intensity = 0): NoteEvent[] {
  if (state === "silencio" || silentPhrase(seed, state, bar)) return [];
  const def = MUSIC[state];
  const out: NoteEvent[] = [];
  const k = Math.max(0, Math.min(1, intensity));
  const root = degree(def, 0) - 12;
  const fifth = degree(def, 4) - 12;
  switch (state) {
    case "menu":
    case "lobby": {
      melody(def, motif(seed, "menu"), bar, "sanfona", 0.45, 12, out);
      for (let i = 0; i < 4; i++) out.push({ t: i, dur: 0.9, inst: "viola", midi: i % 2 ? fifth + 12 : root + 12, vel: 0.35 });
      break;
    }
    case "dia": {
      // Baião: zabumba no 1 e no "e" do 2; triângulo nos oitavos, acento no contratempo.
      out.push({ t: 0, dur: 0.5, inst: "zabumba", midi: root, vel: 0.8 }, { t: 1.5, dur: 0.5, inst: "zabumba", midi: root, vel: 0.55 }, { t: 2, dur: 0.5, inst: "zabumba", midi: root, vel: 0.7 });
      for (let i = 0; i < 8; i++) out.push({ t: i / 2, dur: 0.25, inst: "triangulo", midi: 0, vel: i % 2 ? 0.35 : 0.18 });
      melody(def, motif(seed, "dia"), bar, "sanfona", 0.42, 12, out);
      break;
    }
    case "entardecer": {
      // Pulso grave crescendo pela frase; o motivo do dia em menor.
      const grow = 0.25 + 0.5 * ((bar % 8) / 7);
      for (let i = 0; i < 4; i++) out.push({ t: i, dur: 0.8, inst: "baixo", midi: root, vel: grow });
      melody(def, motif(seed, "dia"), bar, "sanfona", 0.32, 12, out);
      break;
    }
    case "noite":
    case "espectador": {
      if (bar % 2 === 0) out.push({ t: 0, dur: 8, inst: "drone", midi: root, vel: 0.35 });
      if (bar % 4 < 2 || k > 0.3) melody(def, motif(seed, "noite"), bar, "pifano", 0.25 + 0.2 * k, 12, out);
      if (k > 0.2) out.push({ t: 2, dur: 0.3, inst: "caixa", midi: 0, vel: 0.2 + 0.3 * k });
      if (k > 0.6) out.push({ t: 0, dur: 0.3, inst: "bumbo", midi: 0, vel: 0.5 }, { t: 2.5, dur: 0.3, inst: "bumbo", midi: 0, vel: 0.35 });
      break;
    }
    case "chefe": {
      for (let i = 0; i < 8; i++) out.push({ t: i / 2, dur: 0.4, inst: "baixo", midi: i % 4 === 3 ? fifth : root, vel: 0.6 });
      for (let i = 0; i < 4; i++) out.push({ t: i, dur: 0.3, inst: "bumbo", midi: 0, vel: 0.8 });
      out.push({ t: 1, dur: 0.3, inst: "caixa", midi: 0, vel: 0.6 }, { t: 3, dur: 0.3, inst: "caixa", midi: 0, vel: 0.7 }, { t: 3.5, dur: 0.2, inst: "caixa", midi: 0, vel: 0.4 });
      melody(def, motif(seed, "chefe"), bar, "sanfona", 0.4, 0, out);
      break;
    }
    case "encanto": {
      // Ostinato em semicolcheias entre a tônica e a terça.
      for (let i = 0; i < 16; i++) out.push({ t: i / 4, dur: 0.22, inst: "viola", midi: i % 4 === 2 ? degree(def, 2) : degree(def, 0), vel: i % 4 === 0 ? 0.5 : 0.3 });
      out.push({ t: 0, dur: 0.3, inst: "bumbo", midi: 0, vel: 0.6 }, { t: 2, dur: 0.3, inst: "bumbo", midi: 0, vel: 0.6 });
      break;
    }
    case "cerco":
    case "combate": {
      for (let i = 0; i < 4; i++) out.push({ t: i, dur: 0.3, inst: "bumbo", midi: 0, vel: 0.5 + 0.3 * k });
      for (let i = 0; i < 4; i++) out.push({ t: i + 0.5, dur: 0.4, inst: "baixo", midi: root, vel: 0.4 + 0.2 * k });
      if (k > 0.4) for (let i = 0; i < 8; i++) out.push({ t: i / 2, dur: 0.2, inst: "triangulo", midi: 0, vel: 0.25 });
      if (k > 0.7) out.push({ t: 1, dur: 0.3, inst: "caixa", midi: 0, vel: 0.6 }, { t: 3, dur: 0.3, inst: "caixa", midi: 0, vel: 0.6 });
      break;
    }
    case "travessia": {
      // Todos os timbres; o tema do Boitatá (fixo, não sai da seed) em crescendo.
      const theme = [0, 2, 4, 7, 6, 4, 2, 4];
      const grow = Math.min(1, 0.4 + (bar % 16) / 16);
      theme.forEach((d, i) => out.push({ t: i / 2, dur: 0.45, inst: "sanfona", midi: degree(def, d) + 12, vel: 0.45 * grow }));
      out.push({ t: 0, dur: 0.5, inst: "zabumba", midi: root, vel: 0.8 }, { t: 1.5, dur: 0.5, inst: "zabumba", midi: root, vel: 0.6 }, { t: 2, dur: 0.5, inst: "zabumba", midi: root, vel: 0.75 });
      for (let i = 0; i < 8; i++) out.push({ t: i / 2, dur: 0.25, inst: "triangulo", midi: 0, vel: 0.3 });
      for (let i = 0; i < 4; i++) out.push({ t: i, dur: 0.9, inst: "baixo", midi: i % 2 ? fifth : root, vel: 0.5 });
      if (bar % 2 === 0) out.push({ t: 0, dur: 8, inst: "drone", midi: root - 12, vel: 0.3 });
      break;
    }
    case "vitoria": {
      // Fanfarra de sanfona: arpejo maior e a tônica sustentada.
      [0, 2, 4, 7].forEach((d, i) => out.push({ t: i * 0.5, dur: 0.5, inst: "sanfona", midi: degree(def, d), vel: 0.5 }));
      out.push({ t: 2, dur: 2, inst: "sanfona", midi: degree(def, 7), vel: 0.55 }, { t: 0, dur: 0.5, inst: "zabumba", midi: root, vel: 0.8 }, { t: 2, dur: 0.5, inst: "zabumba", midi: root, vel: 0.8 });
      break;
    }
    case "derrota": {
      // Tema lento em menor, descendo.
      const d = 4 - (bar % 5);
      out.push({ t: 0, dur: 3.5, inst: "sanfona", midi: degree(def, d), vel: 0.35 }, { t: 0, dur: 4, inst: "drone", midi: root, vel: 0.25 });
      break;
    }
  }
  return out;
}

/** Frequência de uma nota MIDI. */
export const midiHz = (midi: number): number => 440 * 2 ** ((midi - 69) / 12);
